import type { TFunction } from "i18next";
import {
  resolveLinkTargetOption,
  type LinkTargetOption,
} from "./linkTargetValues";
import type {
  SelectObjectFieldDefinition,
  SelectObjectTypeConfig,
} from "@/shared/components/Form/types";
import { PlatformPagesListQuery } from "@/partner/entities/platformPages";
import { PostsListQuery } from "@/partner/entities/posts";
import { TagsListQuery } from "@/partner/entities/tags";
import { CollectionsListQuery } from "@/partner/entities/contents";
import { ProductsListQuery } from "@/partner/entities/products";
import {
  ContentTypeType,
  LinkTargetTypeType,
  type CollectionsListQueryType,
  type PlatformPagesListQueryType,
  type PostsListQueryType,
  type ProductsListQueryType,
  type TagsListQueryType,
} from "@/types/graphql/partner.generated";

export const LINK_TARGET_OPTION_LABEL_KEYS: Record<LinkTargetOption, string> = {
  PAGE: "linkTarget.option.page",
  POST: "linkTarget.option.post",
  TAG: "linkTarget.option.tag",
  VIDEO: "linkTarget.option.video",
  STREAM: "linkTarget.option.stream",
  COLLECTION: "linkTarget.option.collection",
  PRODUCT: "linkTarget.option.product",
};

const LINK_TARGET_SELECT_TITLE_KEYS: Record<LinkTargetOption, string> = {
  PAGE: "linkTarget.selectTitle.page",
  POST: "linkTarget.selectTitle.post",
  TAG: "linkTarget.selectTitle.tag",
  VIDEO: "linkTarget.selectTitle.video",
  STREAM: "linkTarget.selectTitle.stream",
  COLLECTION: "linkTarget.selectTitle.collection",
  PRODUCT: "linkTarget.selectTitle.product",
};

const LIST_SIZE = 50;

const contentTarget = (
  t: TFunction,
  option: LinkTargetOption,
  type: ContentTypeType
): SelectObjectTypeConfig => ({
  label: t(LINK_TARGET_OPTION_LABEL_KEYS[option]),
  selectTitle: t(LINK_TARGET_SELECT_TITLE_KEYS[option]),
  targetType: LinkTargetTypeType.ContentType,
  queryList: CollectionsListQuery,
  getListVariables: (search) => ({
    page: 0,
    rowsPerPage: LIST_SIZE,
    filter: { search, type },
  }),
  getList: (data: CollectionsListQueryType) => data.contents,
});

/**
 * Field `selectObject` of the link and menu item forms: writes targetType and
 * targetId. Stream is offered only where live streams are enabled.
 */
export function getLinkTargetField(
  t: TFunction,
  { liveEnabled = true }: { liveEnabled?: boolean } = {}
): Pick<SelectObjectFieldDefinition, "targets" | "getTargetOption"> {
  const targets: Partial<Record<LinkTargetOption, SelectObjectTypeConfig>> = {
    PAGE: {
      label: t(LINK_TARGET_OPTION_LABEL_KEYS.PAGE),
      selectTitle: t(LINK_TARGET_SELECT_TITLE_KEYS.PAGE),
      targetType: LinkTargetTypeType.PageType,
      queryList: PlatformPagesListQuery,
      getList: (data: PlatformPagesListQueryType) => data.platformPages,
    },
    POST: {
      label: t(LINK_TARGET_OPTION_LABEL_KEYS.POST),
      selectTitle: t(LINK_TARGET_SELECT_TITLE_KEYS.POST),
      targetType: LinkTargetTypeType.PostType,
      queryList: PostsListQuery,
      getList: (data: PostsListQueryType) => data.posts,
    },
    TAG: {
      label: t(LINK_TARGET_OPTION_LABEL_KEYS.TAG),
      selectTitle: t(LINK_TARGET_SELECT_TITLE_KEYS.TAG),
      targetType: LinkTargetTypeType.TagType,
      queryList: TagsListQuery,
      getList: (data: TagsListQueryType) => data.tags,
    },
    VIDEO: contentTarget(t, "VIDEO", ContentTypeType.VideoType),
    ...(liveEnabled
      ? { STREAM: contentTarget(t, "STREAM", ContentTypeType.StreamType) }
      : {}),
    COLLECTION: contentTarget(t, "COLLECTION", ContentTypeType.CollectionType),
    PRODUCT: {
      label: t(LINK_TARGET_OPTION_LABEL_KEYS.PRODUCT),
      selectTitle: t(LINK_TARGET_SELECT_TITLE_KEYS.PRODUCT),
      targetType: LinkTargetTypeType.ProductType,
      queryList: ProductsListQuery,
      getListVariables: (search) => ({
        page: 0,
        rowsPerPage: LIST_SIZE,
        search,
      }),
      getList: (data: ProductsListQueryType) => ({
        count: data.products.count,
        nodes: data.products.nodes.map((product) => ({
          ...product,
          url: product.siteUrl,
        })),
      }),
    },
  };

  return {
    targets: targets as Record<string, SelectObjectTypeConfig>,
    getTargetOption: (values) =>
      resolveLinkTargetOption(
        values.targetType as LinkTargetTypeType | undefined,
        values.targetUrl as string | undefined
      ),
  };
}
